import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { axiosClient } from '../axiosClient';

export interface Headline {
  id: string;
  text: string;
  link_url: string | null;
  link_text: string | null;
  background_color: string;
  text_color: string;
  is_active: boolean;
  created_by_email: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

interface HeadlineListResponse {
  items: Headline[];
  total: number;
  page: number;
  page_size: number;
}

// ── Admin hooks ───────────────────────────────────────────────────

export function useAdminHeadlines() {
  return useQuery({
    queryKey: ['admin-headlines'],
    queryFn: async () => {
      const { data } = await axiosClient.get<HeadlineListResponse>('/admin/headlines');
      return data;
    },
  });
}

export function useCreateHeadline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      text: string;
      link_url?: string;
      link_text?: string;
      background_color?: string;
      text_color?: string;
    }) => {
      const { data } = await axiosClient.post<Headline>('/admin/headlines', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-headlines'] });
      queryClient.invalidateQueries({ queryKey: ['active-headline'] });
    },
  });
}

export function useUpdateHeadline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      id: string;
      text?: string;
      link_url?: string | null;
      link_text?: string | null;
      background_color?: string;
      text_color?: string;
    }) => {
      const { id, ...body } = payload;
      const { data } = await axiosClient.put<Headline>(`/admin/headlines/${id}`, body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-headlines'] });
      queryClient.invalidateQueries({ queryKey: ['active-headline'] });
    },
  });
}

export function useActivateHeadline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await axiosClient.patch<Headline>(`/admin/headlines/${id}/activate`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-headlines'] });
      queryClient.invalidateQueries({ queryKey: ['active-headline'] });
    },
  });
}

export function useDeleteHeadline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await axiosClient.delete(`/admin/headlines/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-headlines'] });
      queryClient.invalidateQueries({ queryKey: ['active-headline'] });
    },
  });
}

// ── Public hook ────────────────────────────────────────────────────

export function useActiveHeadline() {
  return useQuery({
    queryKey: ['active-headline'],
    queryFn: async () => {
      const { data } = await axiosClient.get<Headline | null>('/headlines/active');
      return data;
    },
    // Refetch every 5 minutes to catch admin changes
    refetchInterval: 5 * 60 * 1000,
  });
}
